import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, PencilSquareIcon, PlusIcon, FunnelIcon, DocumentDuplicateIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DataTable from '../ui/DataTable';
import { COTIZACION_STATES } from '../../assets/data/arrays';
import Pagination from '../ui/Pagination';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_ID_ADMIN } from '../../constants/roleIds';
import LoadingSpinner from '../LoadingSpinner';
import { generarPDFCotizacion } from '../pdf-templates/CotizacionPDF';
import { useURLFilters } from '../../hooks/useURLFilters';
import { User, UsersResponse } from '../../types/users';
import { Button } from '../ui';
import { FilterPanel, FilterSection } from '../ui/FilterPanel';

interface Cotizacion {
  autoIncrementId?: number;
  id: number;
  cotizaciones_id: string;
  origin_cotizacion_id?: string;
  store_name: string;
  first_name?: string;
  last_name: string;
  customer_full_name?: string;
  document: string;
  date_created: string;
  status: string;
  guia: string;
  total: string;
  agente?: {
    id: number;
    first_name: string;
    last_name: string;
    role_id: number;
    role_name: string;
  };
  agente_full_name?: string;
}

// Función auxiliar para capitalizar nombres (primera letra mayúscula)
function capitalizeName(name: string): string {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
}

/* // Función auxiliar para obtener el nombre de la ciudad por código
function getNombreCiudad(codigoCiudad: string) {
  const ciudadObj = (Ciudades as any[]).find((c) => c.codigo === codigoCiudad);
  return ciudadObj ? ciudadObj.ciudad : codigoCiudad;
}

// Función auxiliar para obtener el valor de meta_data por key
function getMeta(meta_data: any[], key: string) {
  const found = meta_data.find((m) => m.key === key);
  return found ? found.value : '';
}

// Función para convertir una imagen a base64 (webp a png)
async function getImageBase64(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

// Función para convertir una imagen local a base64
function getImageBase64FromLocal(url: string): Promise<string> {
  return fetch(url)
    .then(res => res.blob())
    .then(blob => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    }));
} */

const CotizacionTable: React.FC = () => {
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('date_created');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedStates, setSelectedStates] = useState<{ label: string; value: string }[]>([]);
  const [selectedStores] = useState<{ label: string; value: string }[]>([]);
  const [selectedTransportadoras] = useState<{ label: string; value: string }[]>([]);
  const [selectedPaymentMethods] = useState<{ label: string; value: string }[]>([]);
  const [selectedDestinos] = useState<{ label: string; value: string }[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [filterByState, setFilterByState] = useState(false);
  const [filterByDate, setFilterByDate] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [agents, setAgents] = useState<User[]>([]);
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const isAdmin = user?.role_id === ROLE_ID_ADMIN;
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const isInitialLoad = useRef(true);
  const lastFiltersRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);
  //const [selectedCities, setSelectedCities] = useState<{ label: string; value: string }[]>([]);
  //const [filterByStore, setFilterByStore] = useState(false);
  //const [filterByTransportadora, setFilterByTransportadora] = useState(false);
  //const [filterByPaymentMethod, setFilterByPaymentMethod] = useState(false);
  //const [filterByCity, setFilterByCity] = useState(false);
  //const [filterByDestino, setFilterByDestino] = useState(false);

  const [activeFilters, setActiveFilters] = useState({
    state: false, store: false, transportadora: false, paymentMethod: false, destino: false, date: false, agent: false
  });

  const { getFiltersFromURL, updateURL } = useURLFilters();

  const fetchCotizaciones = useCallback(async (
    page: number, 
    limit: number, 
    search = '', 
    sortBy = 'date_created', 
    order = 'desc', 
    states: string[] = [], 
    stores: string[] = [], 
    dateRange?: { start: string; end: string },
    transportadoras: string[] = [],
    paymentMethods: string[] = [],
    destinos: string[] = [],
    agent: string = ''
  ) => {
    // Cancelar la búsqueda anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Esperar a que termine la búsqueda anterior si está en curso
    while (isFetchingRef.current) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Crear nuevo AbortController para esta búsqueda
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isFetchingRef.current = true;
    
    console.log('INICIO fetchCotizaciones');
    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      // Obtener el id del agente desde localStorage
      const user = localStorage.getItem('user');
      let agentId = '';
      if (user) {
        try {
          const userObj = JSON.parse(user);
          agentId = userObj.id || '';
        } catch (e) {
          agentId = '';
        }
      }
      // Solo incluir en la query si hay valores seleccionados (los arrays ya vienen filtrados)
      const statesQuery = states.length > 0 ? `&states=${states.join(',')}` : '';
      const storesQuery = stores.length > 0 ? `&stores=${stores.join(',')}` : '';
      const transportadorasQuery = transportadoras.length > 0 ? `&transportadora=${transportadoras.join(',')}` : '';
      const paymentMethodsQuery = paymentMethods.length > 0 ? `&paymentMethod=${paymentMethods.join(',')}` : '';
      const destinosQuery = destinos.length > 0 ? `&destino_cotizacion=${destinos.join(',')}` : '';
      const dateQuery = dateRange?.start && dateRange?.end ? `&startDate=${dateRange.start}&endDate=${dateRange.end}` : '';
      // Solo agregar el parámetro agent si se selecciona manualmente en el filtro
      const agentQuery = agent ? `&agent=${agent}` : '';
      const url = `/cotizacion?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy}&order=${order}${statesQuery}${storesQuery}${transportadorasQuery}${paymentMethodsQuery}${destinosQuery}${dateQuery}${agentQuery}`;
      console.log('Solicitud de cotizaciones:', { url, headers: { Authorization: `Bearer ${token}` } });
      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController.signal,
      });
      const { data, meta } = response.data;
      console.log('Cotizaciones recibidas:', data);
      const formattedCotizaciones = data.map((cotizacion: Cotizacion, index: number) => {
        const customerName = `${cotizacion.first_name ?? ''} ${cotizacion.last_name ?? ''}`.trim();
        const agenteName = cotizacion.agente 
          ? `${cotizacion.agente.first_name ?? ''} ${cotizacion.agente.last_name ?? ''}`.trim()
          : '';
        
        return {
        ...cotizacion,
        autoIncrementId: index + 1 + (page - 1) * limit,
          customer_full_name: capitalizeName(customerName),
          agente_full_name: agenteName ? capitalizeName(agenteName) : '',
        date_created: new Date(cotizacion.date_created).toLocaleString(),
        };
      });
      setCotizaciones(formattedCotizaciones);
      setCurrentPage(meta.currentPage);
      setTotalPages(meta.totalPages);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error: any) {
      // Ignorar errores de cancelación
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.log('Búsqueda cancelada');
        return;
      }
      console.error('ERROR EN fetchCotizaciones:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [isAdmin]);

  // Cargar lista de agentes
  const loadAgents = useCallback(async () => {
    try {
      const response = await axiosInstance.get<UsersResponse>(`/users/list?limit=1000`);
      if (response.data.success) {
        setAgents(response.data.data || []);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error('Error cargando agentes:', error);
      setAgents([]);
    }
  }, []);

  // Cargar agentes al montar el componente (solo si es admin)
  useEffect(() => {
    if (isAdmin) {
      loadAgents();
    }
  }, [isAdmin, loadAgents]);

  // Función comentada - no se usa actualmente
  // const handleDelete = async (cotizacionId: number) => {
  //   const confirmDelete = await new Promise((resolve) => {
  //     toast((t) => (
  //       <div className="flex flex-col">
  //         <p>¿Confirmas que deseas eliminar esta cotización?</p>
  //         <div className="flex justify-end space-x-2 mt-2">
  //           <button onClick={() => { toast.dismiss(t.id); resolve(true); }} className="px-3 py-1 bg-red-500 text-white rounded">Eliminar</button>
  //           <button onClick={() => { toast.dismiss(t.id); resolve(false); }} className="px-3 py-1 bg-gray-300 rounded">Cancelar</button>
  //         </div>
  //       </div>
  //     ),
  //       { duration: Infinity }
  //     );
  //   });
  //   if (!confirmDelete) return;
  //   try {
  //     const token = localStorage.getItem("accessToken");
  //     await axiosInstance.delete(`/cotizacion/${cotizacionId}`, {
  //       headers: { Authorization: `Bearer ${token}` }
  //     });
  //     toast.success("Cotización eliminada correctamente", { icon: "🗑️" });
  //     fetchCotizaciones(currentPage, pageSize, searchTerm, sortBy, order, selectedStates.map(state => state.value), selectedStores.map(store => store.value), dateRange, selectedTransportadoras.map(t => t.value), selectedPaymentMethods.map(p => p.value), selectedDestinos.map(d => d.value));
  //   } catch (error) {
  //     console.error("Error al eliminar la cotización:", error);
  //     toast.error("Error al eliminar la cotización", { icon: "⚠️" });
  //   }
  // };

  const handleConvertirEnPedido = async (cotizacionId: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      // 1. Obtener detalle de la cotización
      const response = await axiosInstance.get(`/cotizacion/${cotizacionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cotizacion = response.data;
      // 2. Extraer tipo_documento y documento
      let tipo_documento = '';
      let documento = '';
      if (Array.isArray(cotizacion.meta_data)) {
        const tipoDocMeta = cotizacion.meta_data.find((m: any) => m.key === 'tipo_documento');
        if (tipoDocMeta) tipo_documento = tipoDocMeta.value;
        const docMeta = cotizacion.meta_data.find((m: any) => m.key === 'documento');
        if (docMeta) documento = docMeta.value;
      }
      if (!tipo_documento && cotizacion.customer) {
        tipo_documento = cotizacion.customer.tipo_documento || '';
      }
      if (!documento && cotizacion.customer) {
        documento = cotizacion.customer.documento || cotizacion.customer.document || '';
      }
      // 3. Consultar datos completos del cliente
      let clienteCompleto = null;
      if (tipo_documento && documento) {
        try {
          const clienteResp = await axiosInstance.get(`/customers/find?tipo_documento=${tipo_documento}&document=${documento}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          clienteCompleto = clienteResp.data;
        } catch (error) {
          // Si no se encuentra el cliente, continuar con los datos existentes
          clienteCompleto = null;
        }
      }
      // 4. Mezclar datos completos del cliente en la cotización
      if (clienteCompleto) {
        cotizacion.customer = {
          ...cotizacion.customer,
          first_name: clienteCompleto.first_name || cotizacion.customer?.first_name || '',
          last_name: clienteCompleto.last_name || cotizacion.customer?.last_name || '',
          email: clienteCompleto.email || cotizacion.customer?.email || '',
          documento: clienteCompleto.documento || clienteCompleto.document || cotizacion.customer?.documento || cotizacion.customer?.document || '',
          tipo_documento: clienteCompleto.tipo_documento || cotizacion.customer?.tipo_documento || ''
        };
        cotizacion.billingAddress = {
          ...cotizacion.billingAddress,
          first_name: clienteCompleto.first_name || cotizacion.billingAddress?.first_name || '',
          last_name: clienteCompleto.last_name || cotizacion.billingAddress?.last_name || '',
          phone: clienteCompleto.phone || cotizacion.billingAddress?.phone || '',
          address_1: clienteCompleto.dir_1 || cotizacion.billingAddress?.address_1 || '',
          city: clienteCompleto.idciudad_1 || cotizacion.billingAddress?.city || '',
          state: clienteCompleto.idciudad_1 ? clienteCompleto.idciudad_1.substring(0,2) : (cotizacion.billingAddress?.state || '')
        };
        cotizacion.shippingAddress = {
          ...cotizacion.shippingAddress,
          address_1: clienteCompleto.dir_2 || cotizacion.shippingAddress?.address_1 || '',
          city: clienteCompleto.idciudad_2 || cotizacion.shippingAddress?.city || '',
          state: clienteCompleto.idciudad_2 ? clienteCompleto.idciudad_2.substring(0,2) : (cotizacion.shippingAddress?.state || ''),
          phone: clienteCompleto.phone || cotizacion.shippingAddress?.phone || ''
        };
        // Actualizar meta_data con los datos del cliente
        cotizacion.meta_data = [
          ...(Array.isArray(cotizacion.meta_data) ? cotizacion.meta_data.filter((m: any) => m.key !== 'tipo_documento' && m.key !== 'documento') : []),
          { key: 'tipo_documento', value: clienteCompleto.tipo_documento || '' },
          { key: 'documento', value: clienteCompleto.documento || clienteCompleto.document || '' }
        ];
      }
      // 5. Pasar cotización por navigation state (sin status de cotización) y redirigir
      const { status: _statusCotizacion, ...cotizacionSinEstado } = cotizacion
      toast.success("Cotización lista para crear pedido", { icon: "📝", position: 'top-center' });
      navigate('/orders/new', { state: { fromCotizacion: cotizacionSinEstado } });
    } catch (error) {
      console.error("Error al preparar la cotización para pedido:", error);
      toast.error("No se pudo preparar la cotización para pedido", { icon: "⚠️", position: 'top-center' });
    }
  };

  const handleDownloadCotizacion = async (cotizacionId: number) => {
    try {
      toast.loading('Generando PDF de cotización...', { position: 'top-center' });
      await generarPDFCotizacion(cotizacionId);
      toast.dismiss();
      toast.success('Cotización descargada en PDF', { position: 'top-center' });
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar el PDF de la cotización', { position: 'top-center' });
      console.error(error);
    }
  };

  // Debounce para el search term - 1 segundo y solo si tiene 3+ caracteres o está vacío
  useEffect(() => {
    const timer = setTimeout(() => {
      // Solo actualizar si tiene 3+ caracteres o está vacío (para limpiar la búsqueda)
      if (searchTerm.length === 0 || searchTerm.length >= 3) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 1000); // 1 segundo de delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const hasSignificantURLParams = (urlFilters: any) => {
    const hasArrayParams = [
      urlFilters.states,
      urlFilters.stores, 
      urlFilters.transportadoras,
      urlFilters.paymentMethods,
      urlFilters.destinos
    ].some(arr => arr.length > 0);
    
    const hasStringParams = [
      urlFilters.search,
      urlFilters.startDate,
      urlFilters.endDate,
      urlFilters.agent
    ].some(str => str !== '');
    
    const hasNonDefaultParams = 
      urlFilters.page !== 1 ||
      urlFilters.limit !== 30 ||
      urlFilters.sortBy !== 'date_created' ||
      urlFilters.order !== 'desc';
    
    return hasArrayParams || hasStringParams || hasNonDefaultParams;
  };

  // Leer filtros de la URL solo en la carga inicial
  useEffect(() => {
    if (!isInitialLoad.current) return;
    
    const urlFilters = getFiltersFromURL();
    const hasURLParams = hasSignificantURLParams(urlFilters);
    
    if (urlFilters.search !== searchTerm) setSearchTerm(urlFilters.search);
    if (urlFilters.page !== currentPage) setCurrentPage(urlFilters.page);
    if (urlFilters.limit !== pageSize) setPageSize(urlFilters.limit);
    if (urlFilters.sortBy !== sortBy) setSortBy(urlFilters.sortBy);
    if (urlFilters.order !== order) setOrder(urlFilters.order);
    
    if (urlFilters.states.length > 0) {
      setFilterByState(true);
      setActiveFilters(prev => ({ ...prev, state: true }));
      setSelectedStates(urlFilters.states.map(s => ({ label: s, value: s })));
    }
    
    if (urlFilters.stores.length > 0) {
      setActiveFilters(prev => ({ ...prev, store: true }));
      // setSelectedStores(urlFilters.stores.map(s => ({ label: s, value: s })));
    }
    
    if (urlFilters.transportadoras.length > 0) {
      setActiveFilters(prev => ({ ...prev, transportadora: true }));
      // setSelectedTransportadoras(urlFilters.transportadoras.map(t => ({ label: t, value: t })));
    }
    
    if (urlFilters.paymentMethods.length > 0) {
      setActiveFilters(prev => ({ ...prev, paymentMethod: true }));
      // setSelectedPaymentMethods(urlFilters.paymentMethods.map(p => ({ label: p, value: p })));
    }
    
    if (urlFilters.destinos.length > 0) {
      setActiveFilters(prev => ({ ...prev, destino: true }));
      // setSelectedDestinos(urlFilters.destinos.map(d => ({ label: d, value: d })));
    }
    
    if (urlFilters.startDate || urlFilters.endDate) {
      setFilterByDate(true);
      setActiveFilters(prev => ({ ...prev, date: true }));
      setDateRange({ start: urlFilters.startDate, end: urlFilters.endDate });
    }
    
    if (urlFilters.agent) {
      setSelectedAgent(urlFilters.agent);
      setActiveFilters(prev => ({ ...prev, agent: true }));
    }
    
    // Solo ejecutar fetchCotizaciones si hay parámetros en la URL o si es la primera carga
    if (hasURLParams || !hasInitialized) {
      setHasInitialized(true);
    }
    
    isInitialLoad.current = false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resetear a página 1 cuando cambien los filtros (excepto page y currentPage)
  useEffect(() => {
    if (!hasInitialized) return;
    
    // Solo considerar el search si tiene 3+ caracteres o está vacío
    const effectiveSearch = (debouncedSearchTerm.length === 0 || debouncedSearchTerm.length >= 3) 
      ? debouncedSearchTerm 
      : '';
    
    // Crear una clave única para los filtros actuales (sin incluir page)
    const filtersKey = JSON.stringify({
      search: effectiveSearch,
      states: selectedStates.map(s => s.value),
      dateRange,
      transportadoras: selectedTransportadoras.map(t => t.value),
      paymentMethods: selectedPaymentMethods.map(p => p.value),
      destinos: selectedDestinos.map(d => d.value),
      filterByState,
      filterByDate,
      agent: selectedAgent
    });
    
    // Solo resetear si los filtros realmente cambiaron (no page)
    if (lastFiltersRef.current !== filtersKey && lastFiltersRef.current !== '') {
      setCurrentPage(1);
    }
    
    lastFiltersRef.current = filtersKey;
  }, [
    hasInitialized,
    debouncedSearchTerm,
    selectedStates,
    dateRange,
    selectedTransportadoras,
    selectedPaymentMethods,
    selectedDestinos,
    filterByState,
    filterByDate,
    selectedAgent
  ]);

  // Escribir filtros en la URL cuando cambien
  useEffect(() => {
    // Solo actualizar URL si ya se inicializó para evitar bucles
    // Solo incluir search en la URL si tiene 3+ caracteres o está vacío
    if (hasInitialized) {
      const effectiveSearch = (debouncedSearchTerm.length === 0 || debouncedSearchTerm.length >= 3) 
        ? debouncedSearchTerm 
        : '';
      
      updateURL({
        search: effectiveSearch,
        page: currentPage,
        limit: pageSize,
        sortBy,
        order,
        states: selectedStates.map(s => s.value),
        stores: selectedStores.map(s => s.value),
        transportadoras: selectedTransportadoras.map(t => t.value),
        paymentMethods: selectedPaymentMethods.map(p => p.value),
        destinos: selectedDestinos.map(d => d.value),
        startDate: dateRange.start,
        endDate: dateRange.end,
        destinoKey: 'destino_cotizacion', // Especificar que es para cotizaciones
        agent: selectedAgent
      });
    }
  }, [ 
    hasInitialized,
    debouncedSearchTerm, 
    currentPage, 
    pageSize, 
    sortBy, 
    order,
    selectedStates, 
    selectedStores, 
    selectedTransportadoras,
    selectedPaymentMethods, 
    selectedDestinos, 
    dateRange,
    selectedAgent,
    updateURL
  ]);

  // Ref para rastrear los valores anteriores y evitar consultas innecesarias
  const prevFilterValuesRef = useRef<string>('');

  useEffect(() => {
    // Solo hacer fetch si ya se inicializó
    // Solo buscar si tiene 3+ caracteres o está vacío
    if (hasInitialized && (debouncedSearchTerm.length === 0 || debouncedSearchTerm.length >= 3)) {
      // Solo incluir filtros que estén activados Y tengan valores seleccionados
      // Si solo se activa el switch sin seleccionar nada, estos arrays estarán vacíos
      const states = filterByState && selectedStates.length > 0 
        ? selectedStates.map(state => state.value) 
        : [];
      const stores: string[] = []; // No se usa actualmente pero se mantiene para compatibilidad
      const transportadoras: string[] = []; // No se usa actualmente pero se mantiene para compatibilidad
      const paymentMethods: string[] = []; // No se usa actualmente pero se mantiene para compatibilidad
      const destinos: string[] = []; // No se usa actualmente pero se mantiene para compatibilidad
      const dateRangeFilter = filterByDate && dateRange.start && dateRange.end
        ? dateRange 
        : undefined;
      const agent = activeFilters.agent && selectedAgent
        ? selectedAgent
        : '';

      // Crear una clave única con todos los valores de filtro para comparar
      const currentFilterKey = JSON.stringify({
        states: states.length > 0 ? states.sort().join(',') : '',
        stores: stores.length > 0 ? stores.sort().join(',') : '',
        transportadoras: transportadoras.length > 0 ? transportadoras.sort().join(',') : '',
        paymentMethods: paymentMethods.length > 0 ? paymentMethods.sort().join(',') : '',
        destinos: destinos.length > 0 ? destinos.sort().join(',') : '',
        dateRange: dateRangeFilter ? `${dateRangeFilter.start}-${dateRangeFilter.end}` : '',
        agent: agent || '',
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        sortBy,
        order
      });

      // Solo hacer la consulta si realmente cambió algo (no solo la referencia del array)
      // Esto evita consultas cuando solo se activa/desactiva el switch sin seleccionar valores
      if (prevFilterValuesRef.current !== currentFilterKey) {
        prevFilterValuesRef.current = currentFilterKey;
        
        fetchCotizaciones(
          currentPage, 
          pageSize, 
          debouncedSearchTerm, 
          sortBy, 
          order, 
          states,
          stores,
          dateRangeFilter,
          transportadoras,
          paymentMethods,
          destinos,
          agent
        );
      }
    }
    // Comportamiento intencional: NO incluir filterByState, filterByDate, activeFilters.agent
    // Solo incluir las selecciones para que se dispare cuando se seleccionan valores,
    // NO cuando solo se activa el switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage, 
    pageSize, 
    debouncedSearchTerm, 
    sortBy, 
    order, 
    selectedStates, 
    dateRange.start,
    dateRange.end,
    selectedAgent,
    fetchCotizaciones,
    hasInitialized
  ]);

  useEffect(() => {
    console.log('Entraste a la página de cotizaciones, se debería mostrar la tabla.');
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .toggle-checkbox:checked {right: 0;border-color: #3b82f6;}
      .toggle-checkbox:checked + .toggle-label {background-color: #3b82f6;}
      .react-select-container {width: 100%;}
      .react-select__control {border-color: #e5e7eb;boxShadow: none;}
      .react-select__control:hover {border-color: #3b82f6;}
      .react-select__multi-value {background-color: #e5e7eb;border-radius: 0.25rem;}
      .react-select__multi-value__label {color: #374151;padding: 0.25rem 0.5rem;}
      .react-select__multi-value__remove {color: #6b7280;}
      .react-select__multi-value__remove:hover {background-color: #d1d5db;color: #374151;}
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const columns = [
    {
      header: 'ID',
      accessor: 'id' as keyof Cotizacion,
    },
/*     {
      header: 'ORIGEN',
      accessor: 'cotizaciones_id' as keyof Cotizacion,
    }, */
    {
      header: 'Nombre',
      accessor: 'customer_full_name' as keyof Cotizacion,
      width: '150px',
      maxWidth: '150px',
      cell: (row: Cotizacion) => {
        const displayName = row.customer_full_name 
          ? (row.customer_full_name.length > 25 ? row.customer_full_name.substring(0, 25) + '...' : row.customer_full_name)
          : '';
        return (
          <div className="max-w-[150px] truncate" title={row.customer_full_name || ''}>
            {displayName}
          </div>
        );
      },
    },
    // Columna Agente solo visible para admin
    ...(isAdmin ? [{
      header: 'Agente',
      accessor: 'agente_full_name' as keyof Cotizacion,
      width: '150px',
      maxWidth: '150px',
      cell: (row: Cotizacion) => {
        if (!row.agente_full_name) {
          return (
            <div className="max-w-[150px] truncate" title="Sin asignar">
              <span className="text-gray-400 italic">Sin asignar</span>
            </div>
          );
        }
        const displayName = row.agente_full_name.length > 20 
            ? row.agente_full_name.substring(0, 20) + '...' 
          : row.agente_full_name;
        return (
          <div className="max-w-[150px] truncate" title={row.agente_full_name}>
            {displayName}
        </div>
        );
    },
    }] : []),
    {
      header: 'Fecha',
      accessor: 'date_created' as keyof Cotizacion,
    },
    {
      header: 'Estado',
      accessor: 'status' as keyof Cotizacion,
      cell: (row: Cotizacion) => {
        const state = COTIZACION_STATES.find(s => s.value === row.status);
        if (!state) {
          return (
            <span className="text-center text-xs font-medium max-w-[90px] w-full truncate inline-block p-2 rounded-md text-gray-500 border border-sky-500">
              {row.status}
            </span>
          );
        }
        return (
          <span className={`text-center text-xs font-medium max-w-[90px] w-full truncate inline-block p-2 rounded-md ${state.color}`}>
            {state.label}
          </span>
        );
      },
    },
    {
      header: 'Total',
      accessor: 'total' as keyof Cotizacion,
      cell: (row: Cotizacion) => {
        const value = Number(row.total);
        if (isNaN(value)) return row.total;
        return `$ ${value.toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
      },
    },
    {
      header: 'Acciones',
      accessor: 'id' as keyof Cotizacion,
      cell: (row: Cotizacion) => (
        <div className="flex space-x-2">
          <button onClick={() => navigate(`/cotizaciones/${row.id}`)} className="text-green-500 hover:text-green-700" title="Ver">
            <EyeIcon className="h-6 w-6" />
          </button>
          {hasPermission('edit_cotizaciones') && (
            <button onClick={() => navigate(`/cotizaciones/${row.id}/edit`)} 
            className={`text-blue-500 hover:text-blue-700 ${(row.status === 'APROBADA' || row.status === 'VENCIDA') ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Editar"
            disabled={(row.status === 'VENCIDA' || row.status === 'APROBADA')}>
              <PencilSquareIcon className="h-6 w-6" />
            </button>
            
          )}
          {hasPermission('manage_cotizaciones') && (
            <button
              onClick={() => handleConvertirEnPedido(row.id)}
              className={`text-sky-500 hover:text-sky-700 ${(row.status === 'APROBADA' || row.status === 'VENCIDA') ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Convertir en pedido"
              disabled={(row.status === 'VENCIDA' || row.status === 'APROBADA')}>
              <DocumentDuplicateIcon className="h-6 w-6" />
            </button>
          )}

          <button onClick={() => handleDownloadCotizacion(row.id)}
            className="text-orange-500 hover:text-orange-600" title="Descargar cotización">
            <DocumentArrowDownIcon className="h-6 w-6" />
          </button>
        </div>
      ),
    },
  ];

  const handleFilterByState = (checked: boolean) => {
    setFilterByState(checked);
    setActiveFilters(prev => ({ ...prev, state: checked }));
    if (!checked) {
      setSelectedStates([]);
    }
  };

/*   const handleFilterByStore = (checked: boolean) => {
    setFilterByStore(checked);
    setActiveFilters(prev => ({ ...prev, store: checked }));
    if (!checked) {
      setSelectedStores([]);
    }
  };

  const handleFilterByTransportadora = (checked: boolean) => {
    setFilterByTransportadora(checked);
    setActiveFilters(prev => ({ ...prev, transportadora: checked }));
    if (!checked) {
      setSelectedTransportadoras([]);
    }
  };

  const handleFilterByPaymentMethod = (checked: boolean) => {
    setFilterByPaymentMethod(checked);
    setActiveFilters(prev => ({ ...prev, paymentMethod: checked }));
    if (!checked) {
      setSelectedPaymentMethods([]);
    }
  };

  const handleFilterByDestino = (checked: boolean) => {
    setFilterByDestino(checked);
    setActiveFilters(prev => ({ ...prev, destino: checked }));
    if (!checked) {
      setSelectedDestinos([]);
    }
  }; */

  const handleFilterByDate = (checked: boolean) => {
    setFilterByDate(checked);
    setActiveFilters(prev => ({ ...prev, date: checked }));
    if (!checked) {
      setDateRange({ start: '', end: '' });
    }
  };

  const handleFilterByAgent = (checked: boolean) => {
    setActiveFilters(prev => ({ ...prev, agent: checked }));
    if (!checked) {
      setSelectedAgent('');
    }
  };

  // Calcular cantidad de filtros activos (solo cuenta cuando hay valores seleccionados)
  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (searchTerm) count++;
    if (filterByState && selectedStates.length > 0) count++;
    if (filterByDate && dateRange.start && dateRange.end) count++;
    if (activeFilters.agent && selectedAgent) count++;
    return count;
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterByState(false);
    setSelectedStates([]);
    setFilterByDate(false);
    setDateRange({ start: '', end: '' });
    setActiveFilters(prev => ({ ...prev, state: false, date: false, agent: false }));
    setSelectedAgent('');
    setCurrentPage(1);
  };

  // Preparar secciones de filtros para FilterPanel
  const filterSections: FilterSection[] = [
    {
      id: 'quick-filters',
      title: 'Filtros Rápidos',
      filters: [
        {
          id: 'state',
          label: 'Filtrar por estado',
          type: 'switch',
          checked: filterByState,
          onToggle: handleFilterByState,
          selectProps: filterByState ? {
            isMulti: true,
            options: COTIZACION_STATES.map(state => ({ label: state.label, value: state.value })),
            value: selectedStates,
            onChange: (newValue) => setSelectedStates(newValue as { label: string; value: string }[]),
            placeholder: 'Seleccionar estados...'
          } : undefined
        },
        {
          id: 'date',
          label: 'Filtrar por fecha',
          type: 'switch',
          checked: filterByDate,
          onToggle: handleFilterByDate,
          dateRangeProps: filterByDate ? {
            start: dateRange.start,
            end: dateRange.end,
            onStartChange: (value) => setDateRange(prev => ({ ...prev, start: value })),
            onEndChange: (value) => setDateRange(prev => ({ ...prev, end: value }))
          } : undefined
        }
      ]
    }
  ];

  // Agregar filtro de agente solo si es admin
  if (isAdmin) {
    filterSections.push({
      id: 'advanced-filters',
      title: 'Filtros Avanzados',
      filters: [
        {
          id: 'agent',
          label: 'Filtrar por agente',
          type: 'switch',
          checked: activeFilters.agent,
          onToggle: handleFilterByAgent,
          selectProps: activeFilters.agent ? {
            isMulti: false,
            options: agents.map(agent => ({ 
              label: `${agent.first_name} ${agent.last_name}`, 
              value: agent.id.toString() 
            })),
            value: selectedAgent ? { 
              label: agents.find(a => a.id.toString() === selectedAgent) 
                ? `${agents.find(a => a.id.toString() === selectedAgent)?.first_name} ${agents.find(a => a.id.toString() === selectedAgent)?.last_name}`
                : 'Seleccionar agente',
              value: selectedAgent 
            } : null,
            onChange: (newValue) => setSelectedAgent(newValue?.value || ''),
            placeholder: 'Seleccionar agente...'
          } : undefined
        }
      ]
    });
  }

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-full mx-auto">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <h3>Lista de Cotizaciones</h3>
            <div className="flex items-center gap-4">
              {/* Botón de filtros con contador */}
              <div className="relative overflow-visible">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="secondary"
                  leftIcon={FunnelIcon}
                  className="relative"
                >
                  Filtros
                  {getActiveFiltersCount() > 0 && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold bg-blue-600 text-white rounded-full min-w-[20px] flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </Button>
                <FilterPanel
                  isOpen={showFilters}
                  onClose={() => setShowFilters(false)}
                  sections={filterSections}
                  onClearAll={handleClearAllFilters}
                  activeFiltersCount={getActiveFiltersCount()}
                  position="right"
                />
              </div>
              {hasPermission('manage_orders') && (
                <Button
                  onClick={() => navigate('/cotizaciones/new')}
                  variant="success"
                  leftIcon={PlusIcon}
                  fullWidth
                  className="sm:w-auto"
                >
                  Crear
                </Button>
              )}
            </div>
          </div>
          <div className="w-full overflow-hidden mb-20 relative min-h-[200px]">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-20">
                <LoadingSpinner size="large" />
              </div>
            )}
            <div className={`overflow-x-auto ${isLoading ? 'opacity-30 pointer-events-none' : ''}`}>
              <DataTable
                data={cotizaciones}
                columns={columns}
                className="w-full"/>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default CotizacionTable; 
